import {Component} from 'panel';
import moment from 'moment';

import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {dateStringComparator} from 'common/util';
import {Cache} from 'common/util/cache';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';

import {template} from './template';
import css from './index.inline.styl';

export enum AnnotationViewType {
  DEFAULT = `default`,
  PREVIEW = `preview`,
}

export class ViewAnnotationsList extends Component<EmptyObj> {
  static parseDateCache: Cache = new Cache();

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'active-annotation': `json`,
      'active-annotation-group': `json`,
      'annotation-description-draft': `string`,
      'can-write-annotations': `boolean`,
      'editing-annotation': `json`,
      'deleting-annotation': `json`,
      type: {
        enum: Object.values(AnnotationViewType),
        default: AnnotationViewType.DEFAULT,
        type: `string`,
      },
      'time-unit': `string`,
    };
  }

  get config() {
    return {
      css,
      useShadowDom: true,
      template,
      helpers: {
        AnnotationViewType,
        closeAnnotationListPopup: () => {
          this.dispatchEvent(new CustomEvent(`closeAnnotationPopup`));
        },
        createAnnotation: () => {
          this.dispatchEvent(new CustomEvent(`showCreateAnnotation`, {detail: this.attr(`active-annotation`)}));
        },
        getActiveAnnotationGroup: () => {
          const isPreview = this.attr(`type`) === AnnotationViewType.PREVIEW;
          let activeAnnotationGroup = this.attr(`active-annotation-group`) || [];
          activeAnnotationGroup = activeAnnotationGroup.sort(
            dateStringComparator({
              transform: (a) => a.date,
              parseDateCache: ViewAnnotationsList.parseDateCache,
              parseDateConfig: {iso: true},
            }),
          );
          const numAnnotations = activeAnnotationGroup.length;

          // Preview only shows 2, then has a + X more... below
          activeAnnotationGroup = isPreview ? activeAnnotationGroup.slice(0, 2) : activeAnnotationGroup;

          return {activeAnnotationGroup, numAnnotations};
        },
        formatDate: (isoDate) => {
          switch (this.attr(`time-unit`)) {
            case TimeUnit.Day:
            case TimeUnit.Hour:
            case TimeUnit.Minute:
              return moment(isoDate).format(`ha`);
            default:
              return moment(isoDate).format(`ha, MMM Do`);
          }
        },

        edit: (annotation): void => {
          this.dispatchEvent(
            new CustomEvent(`editAnnotation`, {
              detail: {editingAnnotation: annotation, annotationDescriptionDraft: annotation.description},
            }),
          );
        },

        delete: (annotation): void => {
          this.dispatchEvent(new CustomEvent(`deleteAnnotation`, {detail: annotation}));
        },

        handleDescriptionChanged: (ev): void => {
          this.dispatchEvent(new CustomEvent(`updateAnnotationDescriptionDraft`, {detail: ev.target.value}));
        },

        save: () => {
          this.dispatchEvent(new CustomEvent(`saveAnnotation`));
        },

        editCanceled: () => {
          this.dispatchEvent(new CustomEvent(`editCanceled`));
        },

        deleteConfirmed: () => {
          this.dispatchEvent(new CustomEvent(`deleteConfirmed`));
        },

        deleteCanceled: () => {
          this.dispatchEvent(new CustomEvent(`deleteCanceled`));
        },
      },
    };
  }
}

export const ViewAnnotationsListEl = defineAndMakeJsxFactory(`view-annotations-list`, ViewAnnotationsList);
export const ViewAnnotationsListReact = panel2react(`view-annotations-list`, ViewAnnotationsList);
